/*
 * Transport primitives and the session-shaped calls.
 *
 * Every request goes to the same-origin BFF, never to the enterprise origin, so
 * the browser holds no bearer and no CORS negotiation is involved. The path after
 * `/api` is the enterprise path verbatim — `/api/v1/sessions` is `/v1/sessions` —
 * which keeps this file readable against the published contract.
 *
 * Response and request shapes are taken from `schema.ts`, generated from
 * `openapi.json` by `npm run gen:types`. They are indexed through the `paths` map
 * rather than through `components` so a type is bound to the ROUTE that returns
 * it: if an endpoint starts returning a different schema, this file stops
 * compiling instead of quietly mistyping the data.
 *
 * The knowledge-base surface lives in `knowledgeBases.ts` and builds on the
 * verb helpers exported here, so that adding a screen never means touching the
 * transport.
 */
import type { paths } from './schema.ts'

/** Same-origin BFF root. `BASE_URL` always ends in `/`, at root or sub-path. */
export const API_BASE = `${import.meta.env.BASE_URL}api`

/** Response body of a route's 200, read off the generated `paths` map. */
export type Json200<T> = T extends { responses: { 200: { content: { 'application/json': infer B } } } }
  ? B
  : never
/** Response body of a route's 201. */
export type Json201<T> = T extends { responses: { 201: { content: { 'application/json': infer B } } } }
  ? B
  : never

export type MessagesResponse = Json200<paths['/v1/sessions/{session_id}/messages']['get']>
export type Message = MessagesResponse['items'][number]
export type RunDetail = Json200<paths['/v1/runs/{run_id}']['get']>
export type MeContext = Json200<paths['/v1/me/context']['get']>
export type Me = Json200<paths['/v1/me']['get']>

export interface SessionUser {
  id: string
  email: string
  role: string
}

/**
 * The BFF's answer to "is anybody signed in". Identity is deliberately NOT on
 * it: who the caller is comes from `GET /v1/me`, the API's own answer, so the
 * BFF never derives user-facing data out of the sealed credential.
 */
export interface SessionProbe {
  authenticated: boolean
}

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }

  /**
   * The structured error payload, when the API sent one.
   *
   * Enterprise errors arrive either as a bare object or wrapped in FastAPI's
   * `detail`, and a caller that wants the machine-readable fields (which limit was
   * hit, which layer set it) should not have to know which. Callers that only want
   * a sentence keep using `message`.
   */
  detail(): Record<string, unknown> | null {
    const body = this.body
    if (!body || typeof body !== 'object') return null
    const inner = (body as { detail?: unknown }).detail
    if (inner && typeof inner === 'object') return inner as Record<string, unknown>
    return body as Record<string, unknown>
  }
}

/** Header the BFF translates into whatever scope parameter a route declares. */
const SCOPE_HEADER = 'X-Kb-Scope'

let activeScopeId: string | null = null

/** Set the scope every subsequent request acts in (null = the caller's default). */
export function setActiveScope(scopeId: string | null): void {
  activeScopeId = scopeId
}

export function getActiveScope(): string | null {
  return activeScopeId
}

function headers(extra?: Record<string, string>): Headers {
  const result = new Headers(extra)
  if (activeScopeId) result.set(SCOPE_HEADER, activeScopeId)
  return result
}

let unauthorizedHandler: (() => void) | null = null

/**
 * Register the one place a dead session is handled.
 *
 * A 401 can come back from any call, and every screen would otherwise have to
 * thread an `onUnauthorized` prop down to every panel that fetches — which is both
 * noise and a thing to forget. The BFF has already tried a silent refresh by the
 * time a 401 reaches the browser, so a 401 here means the session is genuinely
 * gone and there is exactly one correct response to it.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text()
  const body: unknown = text ? safeJsonParse(text) : null
  if (!res.ok) {
    if (res.status === 401) unauthorizedHandler?.()
    throw new ApiError(res.status, errorMessage(res.status, body), body)
  }
  return body as T
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function errorMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object') {
    const record = body as { error?: unknown; detail?: unknown }
    if (typeof record.error === 'string') return record.error
    if (typeof record.detail === 'string') return record.detail
  }
  if (typeof body === 'string' && body.length > 0) return body
  return `request failed (${status})`
}

export type Query = Record<string, string | number | boolean | undefined>

/** Same-origin URL for a BFF path. Also what a download link points at. */
export function apiUrl(path: string, query?: Query): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }
  return url.toString()
}

export async function apiGet<T>(path: string, query?: Query): Promise<T> {
  return parse<T>(await fetch(apiUrl(path, query), { headers: headers(), credentials: 'same-origin' }))
}

async function send<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const res = await fetch(apiUrl(path, query), {
    method,
    headers: headers(body === undefined ? undefined : { 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  return parse<T>(res)
}

export function apiPost<T>(path: string, body?: unknown, query?: Query): Promise<T> {
  return send<T>('POST', path, body, query)
}

export function apiPatch<T>(path: string, body?: unknown, query?: Query): Promise<T> {
  return send<T>('PATCH', path, body, query)
}

export function apiDelete(path: string, query?: Query): Promise<void> {
  return send<void>('DELETE', path, undefined, query)
}

/**
 * Multipart upload. The `Content-Type` is deliberately NOT set: the browser has
 * to generate it so it carries the boundary it actually used, and setting it by
 * hand produces a body the server cannot parse.
 */
export async function apiUpload<T>(path: string, form: FormData, query?: Query): Promise<T> {
  const res = await fetch(apiUrl(path, query), {
    method: 'POST',
    headers: headers(),
    credentials: 'same-origin',
    body: form,
  })
  return parse<T>(res)
}

// --- session (BFF-owned; the only non-`/v1` calls in the app) ---

export function probeSession(): Promise<SessionProbe> {
  return apiGet<SessionProbe>('/auth/session')
}

export function login(email: string, password: string): Promise<{ user: SessionUser }> {
  return apiPost<{ user: SessionUser }>('/auth/login', { email, password })
}

export function logout(): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>('/auth/logout')
}

// --- enterprise surface, one function per contract route ---

/** The caller's own identity, resolved by the API from the principal. */
export function me(): Promise<Me> {
  return apiGet<Me>('/v1/me')
}

export function meContext(): Promise<MeContext> {
  return apiGet<MeContext>('/v1/me/context')
}

export function listMessages(sessionId: string, limit = 200): Promise<MessagesResponse> {
  return apiGet<MessagesResponse>(
    `/v1/sessions/${encodeURIComponent(sessionId)}/messages`,
    { limit },
  )
}

export function getRun(runId: string): Promise<RunDetail> {
  return apiGet<RunDetail>(`/v1/runs/${encodeURIComponent(runId)}`)
}

/** Absolute URL of a run's event stream. */
export function streamUrl(runId: string): string {
  return `${API_BASE}/v1/runs/${encodeURIComponent(runId)}/events`
}
